/**
 * What the respondent sees once the form is sent — the fields, on their own.
 *
 * Lifted out of `FormSettings.tsx` unchanged so an ending node on the Workflow
 * canvas can offer exactly the same thing rather than a second, subtly
 * different, editor for the same idea. Settings still renders it; the ending
 * modal renders it with `allowContinue`.
 *
 * The one addition is that third kind. "Continue into the follow-up form" only
 * makes sense where a path can be drawn to reach it, so it exists on a node
 * and not in Settings — and it replaces the arrangement that caused the bug:
 * a thank-you page holding `{{continueUrl}}`, non-blank only because an invite
 * connection on another tab happened to mint it. Since 2026-09-27 it also
 * says how they are carried on: a page with a button in the author's words,
 * or straight into the next form with no page at all.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useFormWorkspace } from './FormWorkspace';
import { workingDocumentOf } from '../../api/forms';
import { documentSummary } from './documentSummary';
import { STRINGS } from '../../form-engine/strings';
import { FieldGroup, TokenOption, TokenPicker, useCaretInsert } from './ConnectionEditors';
import { SecInfo, groupsForContainer } from './fieldGroups';
import { FieldDefinition, asksNothing } from '../../shared/types';

export interface CompletionValue {
  mode?: 'message' | 'redirect' | 'continue';
  title?: string;
  body?: string;
  redirectUrl?: string;
  redirectDelay?: number;
  includeSubmissionId?: boolean;
  allowAnother?: boolean;
  allowDocumentDownload?: boolean;
  /** Let the respondent edit their answers for a while after submitting (P6). */
  allowEdit?: { ttlHours: number };
  /** `continue` only: the words on the button into the next form. Blank is the default. */
  continueLabel?: string;
  /** `continue` only: no thank-you page — the next form opens the moment they submit. */
  continueStraight?: boolean;
}

/**
 * What the thank-you page's words can be filled with — see `thankYouTokens`.
 * Without it the boxes carry no picker; tokens typed by hand still work.
 */
export interface CompletionTokens {
  /** The form's answers, as the page reads them. */
  answers: FieldGroup[];
  /** Values the steps above this ending hand on (`{{inviteUrl}}`, a ticket id). */
  published: TokenOption[];
}

/**
 * Answers that do not read as text on the thank-you page. The page fills its
 * words itself (`fillTokens` in PublicFormPage.tsx) with a flat lookup and
 * `String()`, so a Name or Address answer — an object — would print
 * "[object Object]" and one of its parts would print nothing; a grid and a
 * payment are objects too. Offering them would be offering a broken page.
 */
const NOT_TEXT_ON_PAGE = new Set(['file', 'name', 'address', 'matrix', 'payment']);

/**
 * The picker's answers for the thank-you page: the main form's questions,
 * arranged the way the form is built. A repeating level's answers are lists
 * under the level's own key, so they are not offered either.
 */
export function thankYouTokens(
  fields: FieldDefinition[], sections: SecInfo[] = [], published: TokenOption[] = [],
): CompletionTokens {
  const usable = fields.filter((f) => !asksNothing(f.input_type) && !NOT_TEXT_ON_PAGE.has(f.input_type));
  const answers = groupsForContainer('order', usable, sections, undefined, { strayLabel: sections.length ? 'Other fields' : 'Form fields' })
    .map((g) => ({ label: g.label, options: g.opts.map((o) => ({ key: o.v, label: o.l })) }));
  return { answers, published };
}

/** A box on the thank-you page with "+ Insert a form value…" under it. */
function TokenField({ id, label, value, onChange, groups, placeholder, multiline, children }: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  /** null: no picker, as when the caller has no form to offer values from */
  groups: FieldGroup[] | null;
  placeholder?: string;
  multiline?: boolean;
  children?: ReactNode;
}) {
  const [ref, insert] = useCaretInsert(value, onChange);
  return (
    <div className="fs-field">
      <label htmlFor={id}>{label}</label>
      {multiline
        ? <textarea ref={ref} id={id} rows={3} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
        : <input ref={ref} id={id} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />}
      {groups && <TokenPicker groups={groups} onPick={insert} label={`Insert a form value into ${label}`} />}
      {children}
    </div>
  );
}

export default function CompletionFields({ value, onChange, allowContinue, idPrefix = 'fs', tokens }: {
  value: CompletionValue;
  onChange: (next: CompletionValue) => void;
  /** Offer "continue into the follow-up form" — an ending node, not Settings. */
  allowContinue?: boolean;
  /** Keeps label/input ids unique when two copies are on one page. */
  idPrefix?: string;
  /** What the pickers under the page's words offer; absent, there are none. */
  tokens?: CompletionTokens;
}) {
  // The form this ending belongs to, for naming the document it would offer.
  // Every caller renders inside the workspace, which is where the form lives.
  const { form } = useFormWorkspace();
  const mode = value.mode || 'message';
  const set = (patch: Partial<CompletionValue>) => onChange({ ...value, ...patch });
  const id = (k: string) => `${idPrefix}-${k}`;
  /**
   * A continue ending that skips its page (2026-09-27). The page's words are
   * then only asked for when a PDF on offer holds them there, as it holds a
   * redirect — nobody should be asked to write a page no one will see.
   */
  const straight = mode === 'continue' && !!value.continueStraight;
  const pageShown = !straight || !!value.allowDocumentDownload;
  /**
   * Each box offers what the page fills THERE. The message and the button are
   * given the submission id and the heading is not (PublicFormPage.tsx), and
   * a step's value reaches the page only when the heading or the message
   * names it (`completionTokens` on the server), so the button is not offered
   * one.
   */
  const submissionId: FieldGroup = { label: 'Submission', options: [{ key: 'submissionId', label: 'Submission ID' }] };
  const fromSteps: FieldGroup[] = tokens?.published.length
    ? [{ label: 'From other connections', options: tokens.published }] : [];
  const pick = tokens && {
    heading: [...tokens.answers, ...fromSteps],
    body: [...tokens.answers, submissionId, ...fromSteps],
    button: [...tokens.answers, submissionId],
  };

  return (
    <>
      <label className={`fs-choice ${mode === 'message' ? 'on' : ''}`}>
        <input type="radio" checked={mode === 'message'} onChange={() => set({ mode: 'message' })} />
        <span><b>Show a thank-you page</b><small>They stay on the form and see a confirmation.</small></span>
      </label>
      <label className={`fs-choice ${mode === 'redirect' ? 'on' : ''}`}>
        <input type="radio" checked={mode === 'redirect'} onChange={() => set({ mode: 'redirect' })} />
        <span><b>Redirect to another page</b><small>The message below shows briefly first, then they are sent on.</small></span>
      </label>
      {allowContinue && (
        <label className={`fs-choice ${mode === 'continue' ? 'on' : ''}`}>
          <input type="radio" checked={mode === 'continue'} onChange={() => set({ mode: 'continue' })} />
          <span>
            <b>Continue into the follow-up form</b>
            <small>
              Hands the link straight back instead of emailing it. Needs a step above this one
              that makes the link.
            </small>
          </span>
        </label>
      )}
      {/*
        How a continue ending carries them on (Ben, 2026-09-27): a page in the
        author's own words with a button of their own — or no page at all.
      */}
      {allowContinue && mode === 'continue' && (
        <div className="fs-choice-body">
          <label className="fs-check">
            <input
              type="checkbox" checked={straight}
              onChange={(e) => set({ continueStraight: e.target.checked || undefined })}
            />
            <span>
              <b>Skip the thank-you page</b>
              <small>
                The follow-up form opens the moment they submit. If the link could not be made,
                they see a thank-you page instead.
              </small>
            </span>
          </label>
          {/*
            Ticked, but not what happens: the PDF's button lives on the page,
            so the page is shown (PublicFormPage.tsx, `holdForDocument`). The
            checkbox that decides it sits at the foot of the inspector, out of
            sight — so the reason, and the way out, are said here, where the
            author is looking (Ben, 2026-09-28).
          */}
          {straight && value.allowDocumentDownload && (
            <div className="fs-consequence warn" role="note" style={{ marginTop: 10 }}>
              <b>The page is still shown.</b> “Let them download a PDF of their answers” is on
              below, and its button is on the thank-you page — so they see the page first and carry
              on with its button. Stop offering the PDF to skip the page.
              <div style={{ marginTop: 8 }}>
                <button type="button" className="fs-btn sm" onClick={() => set({ allowDocumentDownload: false })}>
                  Stop offering the PDF
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {pageShown && (
        <>
          <TokenField
            id={id('thanks-title')} label="Thank-you heading" placeholder="Thank You!"
            value={value.title || ''} onChange={(title) => set({ title })}
            groups={pick ? pick.heading : null}
          />
          <TokenField
            id={id('thanks-body')} label="Thank-you message" multiline
            placeholder="Thanks {{firstName}} — we have your submission ({{submissionId}}) and will be in touch."
            value={value.body || ''} onChange={(body) => set({ body })}
            groups={pick ? pick.body : null}
          />
        </>
      )}
      {mode === 'continue' && pageShown && (
        <TokenField
          id={id('continue-label')} label="Button text" placeholder={STRINGS.continueButton}
          value={value.continueLabel || ''} onChange={(continueLabel) => set({ continueLabel })}
          groups={pick ? pick.button : null}
        >
          <div className="hint">The button that takes them into the follow-up form. {'{{tokens}}'} work here too.</div>
        </TokenField>
      )}

      {mode === 'redirect' ? (
        <>
          <div className="fs-field">
            <label htmlFor={id('redirect')}>Redirect to</label>
            <input
              id={id('redirect')} placeholder="https://example.com/thanks"
              value={value.redirectUrl || ''} onChange={(e) => set({ redirectUrl: e.target.value })}
            />
            <div className="hint">Must start with http:// or https://.</div>
          </div>
          <div className="fs-row">
            <div className="fs-field">
              <label htmlFor={id('delay')}>Wait before redirecting</label>
              <input
                id={id('delay')} inputMode="numeric" value={String(value.redirectDelay ?? 0)}
                onChange={(e) => set({ redirectDelay: Number(e.target.value.replace(/[^0-9]/g, '')) || 0 })}
              />
              <div className="hint">Seconds.</div>
            </div>
          </div>
          <label className="fs-check">
            <input
              type="checkbox" checked={value.includeSubmissionId !== false}
              onChange={(e) => set({ includeSubmissionId: e.target.checked })}
            />
            <span>
              <b>Append the submission id to the URL</b>
              <small>Adds <code>?submissionId=…</code> so the destination knows which submission arrived.</small>
            </span>
          </label>
        </>
      ) : !straight && (
        <label className="fs-check">
          <input
            type="checkbox" checked={value.allowAnother !== false}
            onChange={(e) => set({ allowAnother: e.target.checked })}
          />
          <span>
            <b>Offer a “Submit another” button</b>
            <small>For forms one person fills in repeatedly.</small>
          </span>
        </label>
      )}

      {/*
        Offered on the redirect path too. A redirect that shows the message
        first is still a page somebody is looking at, and "let me keep a copy
        of what I just signed" is exactly the moment they want it.
      */}
      <label className="fs-check">
        <input
          type="checkbox" checked={!!value.allowDocumentDownload}
          onChange={(e) => set({ allowDocumentDownload: e.target.checked })}
        />
        <span>
          <b>Let them download a PDF of their answers</b>
          <small>
            {/*
              WHICH PDF is the form's setting, not this ending's — so this says
              what will be offered rather than offering a choice that would let
              two endings hand out two different documents.
            */}
            A button on the thank-you page. It offers <b>{documentSummary(workingDocumentOf(form))}</b>
            {' · '}<Link to={`/admin/forms/${form.id}/document`}>change on Document</Link>. The link is
            private to that submission and lives as long as the Document tab says — switching this off
            stops any link already handed out.
          </small>
        </span>
      </label>

      {/*
        Edit after submit (P6). Offered beside the download for the same
        reason: it is a link on the page they are looking at, whatever the
        mode. The window is hours because "fix a typo the same day" is the
        case; a year is the ceiling the server stores.
      */}
      <label className="fs-check">
        <input
          type="checkbox" checked={!!value.allowEdit}
          onChange={(e) => set({ allowEdit: e.target.checked ? { ttlHours: value.allowEdit?.ttlHours || 24 } : undefined })}
        />
        <span>
          <b>Let them edit their response afterwards</b>
          <small>
            An “Edit your response” link on the thank-you page and in the confirmation email,
            good for a limited time. Their changes are saved as a revision in the submission’s
            history; nothing is re-sent unless you re-send it. Switching this off stops any link
            already handed out.
          </small>
        </span>
      </label>
      {value.allowEdit && (
        <div className="fs-row" style={{ marginTop: -4 }}>
          <div className="fs-field">
            <label htmlFor={id('edit-hours')}>Editable for</label>
            <input
              id={id('edit-hours')} inputMode="numeric" value={String(value.allowEdit.ttlHours ?? 24)}
              onChange={(e) => {
                const n = Number(e.target.value.replace(/[^0-9]/g, ''));
                set({ allowEdit: { ttlHours: Math.min(8760, Math.max(0, n)) } });
              }}
              onBlur={() => { if (!value.allowEdit?.ttlHours) set({ allowEdit: { ttlHours: 24 } }); }}
            />
            <div className="hint">Hours after submitting. 24 is a day; 168 is a week.</div>
          </div>
        </div>
      )}
    </>
  );
}
